import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  api,
  ApiError,
  type ReferenceData,
  type ReminderRule,
  type ReminderRunOutcome,
} from '../api/client'
import { SearchSelect, type SearchOption } from '../components/SearchSelect'
import '../components/ComplianceEditor.css'
import './Reminders.css'

/**
 * Reminder rules — what gets warned about, how early, and to whom.
 *
 * A rule describes a SHAPE, never a list of vehicles: an obligation, optionally
 * one site, and a lead time. Vehicles imported next month are covered without
 * anybody remembering to add them, which is the failure a hand-kept list always
 * eventually has.
 *
 * The screen leans on one property of the server, and it is worth naming because
 * it decides the design: a given fact is reminded about exactly once. That is why
 * "Run now" is an ordinary button rather than something behind a confirmation —
 * pressing it twice sends nothing the first press did not, so there is nothing to
 * warn about. It is also why the history below reads as a ledger rather than a log.
 */

function obligationOptions(reference: ReferenceData | undefined): SearchOption[] {
  return (reference?.complianceTypes ?? []).map((t) => ({
    value: t.complianceTypeId!,
    label: t.displayName ?? '',
  }))
}

function siteOptions(reference: ReferenceData | undefined): SearchOption[] {
  return (reference?.sites ?? []).map((s) => ({ value: s.siteId!, label: s.name ?? '' }))
}

/** Day-first, as every other date on this site is. */
function formatDate(iso: string | null | undefined) {
  if (!iso) return null
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}-${m}-${y}`
}

/**
 * A send is an event, so it carries a time as well as a day.
 *
 * Local, deliberately: the server stores UTC and "reminded at 03:40" would be
 * read as the middle of the night by somebody who saw the mail arrive at 09:10.
 */
function formatSent(iso: string | null | undefined) {
  if (!iso) return '—'
  const at = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${pad(at.getDate())}-${pad(at.getMonth() + 1)}-${at.getFullYear()} ` +
    `${pad(at.getHours())}:${pad(at.getMinutes())}`
  )
}

export function Reminders() {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<ReminderRule | 'new' | null>(null)
  const [confirmingRetire, setConfirmingRetire] = useState<number | null>(null)
  const [runOutcome, setRunOutcome] = useState<ReminderRunOutcome | null>(null)
  const [historyRuleId, setHistoryRuleId] = useState<number | ''>('')

  const rules = useQuery({ queryKey: ['reminders'], queryFn: () => api.getReminderRules(true) })
  const reference = useQuery({ queryKey: ['reference'], queryFn: api.getReference })

  // Asked before the Run button is offered. A control that fails when pressed is
  // worse than one that says why it cannot work — the same reasoning as the
  // report's Send button, which reads the same endpoint.
  const status = useQuery({
    queryKey: ['email', 'status'],
    queryFn: api.getEmailStatus,
    staleTime: 5 * 60_000,
    retry: false,
  })

  const history = useQuery({
    queryKey: ['reminders', 'history', historyRuleId],
    queryFn: () => api.getReminderHistory(historyRuleId === '' ? undefined : historyRuleId, 1, 25),
  })

  const run = useMutation({
    mutationFn: api.runReminders,
    onSuccess: (outcome) => {
      setRunOutcome(outcome)
      // Both lists move: a run changes each rule's sent count and adds to the ledger.
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
    },
  })

  const retire = useMutation({
    mutationFn: (id: number) => api.deleteReminderRule(id),
    onSuccess: () => {
      setConfirmingRetire(null)
      queryClient.invalidateQueries({ queryKey: ['reminders'] })
    },
  })

  const configured = status.data?.isConfigured ?? false
  const list = rules.data ?? []

  const ruleFilterOptions: SearchOption[] = list.map((r) => ({
    value: r.reminderRuleId!,
    label: r.name ?? '',
  }))

  return (
    <main className="remind">
      <header className="remind__head">
        <div className="remind__headText">
          <p className="eyebrow">Fleet Due Date</p>
          <h1 className="remind__title">Reminders</h1>
          <p className="remind__sub">
            Standing instructions that watch the register and warn people before a certificate
            lapses.
            {configured && status.data?.fromAddress && (
              <>
                {' '}
                Sent from <span className="mono">{status.data.fromAddress}</span>.
              </>
            )}
          </p>
        </div>

        <div className="remind__acts">
          <button
            type="button"
            className="btn remind__btn"
            disabled={!configured || run.isPending}
            onClick={() => run.mutate()}
            title={
              configured
                ? 'Sends anything newly due. Safe to press more than once.'
                : 'No mail relay is configured.'
            }
          >
            {run.isPending ? 'Running…' : 'Run now'}
          </button>

          <button
            type="button"
            className="btn btn--primary remind__btn"
            onClick={() => {
              setRunOutcome(null)
              setConfirmingRetire(null)
              setEditing('new')
            }}
          >
            New rule
          </button>
        </div>
      </header>

      {/* Said once, at the top, rather than as a surprise when a rule never fires. */}
      {status.isFetched && !configured && (
        <p className="remind__note remind__note--warn">
          {status.data?.message ?? 'No mail relay is configured.'} Rules can be written now;
          they start sending as soon as one is. Nothing is marked as reminded in the meantime,
          so whatever is due today is still due when the relay arrives.
        </p>
      )}

      {run.isError && (
        <p className="remind__note remind__note--bad" role="alert">
          {(run.error as Error).message || 'The run could not be completed.'}
        </p>
      )}

      {runOutcome && (
        <div
          className={`remind__note${runOutcome.failed ? ' remind__note--warn' : ' remind__note--ok'}`}
          role="status"
        >
          <b>
            {runOutcome.itemsReminded === 0
              ? 'Nothing new was due.'
              : `${runOutcome.itemsReminded} reminded across ${runOutcome.rulesWithSomethingDue} rule${
                  runOutcome.rulesWithSomethingDue === 1 ? '' : 's'
                }.`}
          </b>{' '}
          {/* Worth spelling out — it is the reason pressing the button again is harmless. */}
          {runOutcome.itemsReminded === 0 &&
            runOutcome.rulesConsidered > 0 &&
            'Everything currently due has already been warned about.'}
          {runOutcome.skipped > 0 && ` ${runOutcome.skipped} were already covered.`}

          {(runOutcome.errors?.length ?? 0) > 0 && (
            <ul className="remind__errs">
              {runOutcome.errors!.map((message, i) => (
                <li key={i}>{message}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {editing && (
        <RuleForm
          // Keyed, so switching straight from one rule to another rebuilds the
          // fields. Without it React keeps the mounted state and the form goes on
          // showing the previous rule's values.
          key={editing === 'new' ? 'new' : editing.reminderRuleId}
          rule={editing === 'new' ? null : editing}
          obligations={obligationOptions(reference.data)}
          sites={siteOptions(reference.data)}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            queryClient.invalidateQueries({ queryKey: ['reminders'] })
          }}
        />
      )}

      <section className="remind__sec">
        <h2 className="remind__h2">Rules</h2>

        {rules.isLoading && <p className="remind__empty">Loading…</p>}

        {!rules.isLoading && list.length === 0 && (
          <p className="remind__empty">
            No rules yet. A rule watches one obligation — or all seven — and warns you a set
            number of days before anything under it expires.
          </p>
        )}

        {list.length > 0 && (
          <div className="remind__rules">
            {list.map((r) => (
              <article
                key={r.reminderRuleId}
                className={`remind__rule${r.isEnabled ? '' : ' remind__rule--off'}`}
              >
                <div className="remind__ruleTop">
                  <h3 className="remind__ruleName">{r.name}</h3>
                  {!r.isEnabled && <span className="remind__paused">Paused</span>}
                </div>

                <p className="remind__what">
                  <b>{r.complianceTypeName ?? 'Any obligation'}</b>
                  {r.siteName ? ` at ${r.siteName}` : ' · whole fleet'} · <b>{r.daysBefore} days</b>{' '}
                  before expiry
                  {r.includeUnknown && ' · including unrecorded'}
                </p>

                <p className="remind__to mono">{r.toAddresses}</p>

                <p className="remind__stat">
                  {r.sentCount === 0 ? (
                    'Has not fired yet'
                  ) : (
                    <>
                      <b>{r.sentCount}</b> reminded · last {formatSent(r.lastSentUtc)}
                    </>
                  )}
                </p>

                {confirmingRetire === r.reminderRuleId ? (
                  <div className="remind__confirm">
                    <p>
                      <b>Retire “{r.name}”?</b> It stops sending. Everything it has already
                      reminded about is kept, so the record of what was warned, and when,
                      survives.
                    </p>

                    {retire.error && (
                      <p className="remind__note remind__note--bad" role="alert">
                        {(retire.error as Error).message}
                      </p>
                    )}

                    <div className="remind__ruleActs">
                      <button
                        type="button"
                        className="btn btn--danger"
                        disabled={retire.isPending}
                        onClick={() => retire.mutate(r.reminderRuleId!)}
                      >
                        {retire.isPending ? 'Retiring…' : 'Yes, retire it'}
                      </button>
                      <button
                        type="button"
                        className="btn"
                        disabled={retire.isPending}
                        onClick={() => setConfirmingRetire(null)}
                      >
                        Keep it
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="remind__ruleActs">
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        setRunOutcome(null)
                        setConfirmingRetire(null)
                        setEditing(r)
                      }}
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      className="btn"
                      disabled={r.sentCount === 0}
                      title={
                        r.sentCount === 0
                          ? 'This rule has not sent anything yet.'
                          : 'Show only what this rule has reminded about'
                      }
                      onClick={() => setHistoryRuleId(r.reminderRuleId!)}
                    >
                      History
                    </button>

                    <button
                      type="button"
                      className="btn btn--danger"
                      onClick={() => {
                        retire.reset()
                        setConfirmingRetire(r.reminderRuleId!)
                      }}
                    >
                      Retire
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="remind__sec">
        <div className="remind__secHead">
          <h2 className="remind__h2">Recently reminded</h2>

          {ruleFilterOptions.length > 1 && (
            <div className="remind__filter">
              <span className="eyebrow">Rule</span>
              <SearchSelect
                options={ruleFilterOptions}
                value={historyRuleId}
                onChange={(v) => setHistoryRuleId(v)}
                emptyLabel="Every rule"
                placeholder="Search rules…"
              />
            </div>
          )}
        </div>

        <p className="remind__empty">
          Each obligation appears once. It comes back only if the certificate is renewed and
          the new date comes round — which is what stops one vehicle being warned about every
          day until somebody acts on it.
        </p>

        {(history.data?.items?.length ?? 0) === 0 ? (
          <p className="remind__empty">Nothing has been sent yet.</p>
        ) : (
          <div className="remind__scroll">
            {/* Fixed layout and a colgroup: auto layout sizes every column to its
                widest cell, which is how this table earns a horizontal scrollbar. */}
            <table className="remind__tbl">
              <colgroup>
                <col className="remind__cReg" />
                <col className="remind__cOblig" />
                <col className="remind__cDate" />
                <col className="remind__cRule" />
                <col className="remind__cSent" />
                <col className="remind__cState" />
              </colgroup>
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Obligation</th>
                  <th>Expiry</th>
                  <th>Rule</th>
                  <th>Warned</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {history.data!.items!.map((h) => (
                  <tr key={h.reminderSentId}>
                    <td className="mono">
                      {h.registration ? (
                        <Link to={`/vehicles/${h.registration}`}>{h.registration}</Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{h.complianceTypeName ?? '—'}</td>
                    <td className="mono">{formatDate(h.expiryDate) ?? 'not recorded'}</td>
                    <td>{h.ruleName}</td>
                    <td className="mono">{formatSent(h.sentAtUtc)}</td>
                    <td>
                      {/*
                        Whether the message actually left, not merely that a
                        reminder was decided upon. The two come apart when a relay
                        is down, and somebody asking "was he told?" means the first.
                      */}
                      {h.deliveryStatus === 2 ? (
                        <span className="remind__chip remind__chip--ok">Sent</span>
                      ) : h.deliveryStatus === 3 ? (
                        <span className="remind__chip remind__chip--bad">Failed</span>
                      ) : (
                        <span className="remind__chip">Queued</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(history.data?.totalRows ?? 0) > (history.data?.items?.length ?? 0) && (
          <p className="remind__empty">
            The most recent {history.data!.items!.length} of {history.data!.totalRows}.
          </p>
        )}
      </section>
    </main>
  )
}

function RuleForm({
  rule,
  obligations,
  sites,
  onCancel,
  onSaved,
}: {
  rule: ReminderRule | null
  obligations: SearchOption[]
  sites: SearchOption[]
  onCancel: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(rule?.name ?? '')
  const [typeId, setTypeId] = useState<number | ''>(rule?.complianceTypeId ?? '')
  const [siteId, setSiteId] = useState<number | ''>(rule?.siteId ?? '')
  const [days, setDays] = useState(String(rule?.daysBefore ?? 30))
  const [addresses, setAddresses] = useState(rule?.toAddresses ?? '')
  const [includeUnknown, setIncludeUnknown] = useState(rule?.includeUnknown ?? false)
  const [isEnabled, setIsEnabled] = useState(rule?.isEnabled ?? true)

  const save = useMutation({
    mutationFn: () =>
      api.saveReminderRule({
        reminderRuleId: rule?.reminderRuleId ?? null,
        name: name.trim(),
        complianceTypeId: typeId === '' ? null : typeId,
        siteId: siteId === '' ? null : siteId,
        daysBefore: Number(days) || 0,
        toAddresses: addresses.trim(),
        includeUnknown,
        isEnabled,
        // Sent back untouched. Without it the proc guards a token the client
        // never had, and two people editing one rule overwrite each other in
        // silence.
        rowVersion: rule?.rowVersion ?? null,
      }),
    onSuccess: onSaved,
  })

  const daysNum = Number(days)
  const valid = name.trim() !== '' && addresses.trim() !== '' && daysNum >= 1 && daysNum <= 365
  const conflict = save.error instanceof ApiError && save.error.isConflict

  return (
    <form
      className="remind__form"
      onSubmit={(e) => {
        e.preventDefault()
        if (valid) save.mutate()
      }}
    >
      <h2 className="remind__h2">{rule ? `Edit “${rule.name}”` : 'New rule'}</h2>

      <div className="remind__grid">
        <label className="remind__field">
          <span className="eyebrow">Name</span>
          <input
            className="remind__input"
            value={name}
            placeholder="Fitness, one month out"
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className="remind__field">
          <span className="eyebrow">Days before expiry</span>
          <input
            className="remind__input"
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(e) => setDays(e.target.value)}
          />
          <span className="remind__hint">
            Two lead times means two rules — a 60-day heads-up and a 7-day chase usually go to
            different people.
          </span>
        </label>

        <div className="remind__field">
          <span className="eyebrow">Obligation</span>
          <SearchSelect
            options={obligations}
            value={typeId}
            onChange={(v) => setTypeId(v)}
            emptyLabel="Any obligation"
            placeholder="Search obligations…"
          />
        </div>

        <div className="remind__field">
          <span className="eyebrow">Site</span>
          <SearchSelect
            options={sites}
            value={siteId}
            onChange={(v) => setSiteId(v)}
            emptyLabel="Whole fleet"
            placeholder="Search sites…"
          />
        </div>
      </div>

      <label className="remind__field">
        <span className="eyebrow">Send to</span>
        <input
          className="remind__input"
          value={addresses}
          placeholder="ops@company.com, depot@company.com"
          onChange={(e) => setAddresses(e.target.value)}
        />
        <span className="remind__hint">
          Comma separated. Each recipient gets their own message, so the distribution list is
          not disclosed to everybody on it.
        </span>
      </label>

      <label className="remind__check">
        <input
          type="checkbox"
          checked={includeUnknown}
          onChange={(e) => setIncludeUnknown(e.target.checked)}
        />
        <span>
          Also warn about obligations with nothing recorded
          <span className="remind__hint">
            These have no date, so they cannot be thirty days away. Including them asks a
            different and much louder question — 573 vehicles have no All India Permit
            recorded at all.
          </span>
        </span>
      </label>

      <label className="remind__check">
        <input
          type="checkbox"
          checked={isEnabled}
          onChange={(e) => setIsEnabled(e.target.checked)}
        />
        <span>
          Active
          <span className="remind__hint">
            A paused rule is skipped by every run, and nothing it would have sent is marked as
            reminded — so switching it back on picks up whatever came due meanwhile.
          </span>
        </span>
      </label>

      {conflict && (
        <p className="remind__note remind__note--bad" role="alert">
          Somebody else changed this rule first. Reload and try again.
        </p>
      )}

      {save.error && !conflict && (
        <p className="remind__note remind__note--bad" role="alert">
          {(save.error as Error).message || 'The rule could not be saved.'}
        </p>
      )}

      <div className="remind__formActs">
        <button type="submit" className="btn btn--primary" disabled={!valid || save.isPending}>
          {save.isPending ? 'Saving…' : rule ? 'Save rule' : 'Create rule'}
        </button>
        <button type="button" className="btn" onClick={onCancel} disabled={save.isPending}>
          Cancel
        </button>
      </div>
    </form>
  )
}
